import { useCallback, useEffect, useMemo, useRef, useState, type RefObject } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import type {
  ClaimContact,
  CompleteRegistrationFromInvitation,
  CompleteRegistrationUserData,
  CreateSignatureRequest,
  GetSignatureRequestStatus,
  InvitationIdentityConflict,
  InvitationOrganization,
  InvitationPreAuth,
  InvitationTokenData,
  ListRequiredWaivers,
  LoadUserProfile,
  MembershipModel,
  RequestNewInvite,
  SendContactClaimOtp,
  SendOtp,
  SetSession,
  SubscribeSignatureRequestStatus,
  VerifyInvitationToken,
  VerifyOtp,
  UploadIdentityDocument,
} from '@/modules/auth/use-cases'
import type {
  LoadCurrentDocument,
  RecordLegalAcceptance,
} from '@/modules/legal/use-cases'
import type { EmitFunnelEvent, LegalDocument } from '@/shared/domain'
import {
  AccountExistsError,
  ComplianceIncompleteError,
  ContactClaimRequiredError,
  DuplicateContactError,
  IdentityMergeConflictError,
  EmailLinkedToDifferentAccountError,
  NetworkError,
  PlaceholderMergeLedgerConflictError,
  UserNotFoundError,
  hashStorageKeyPart,
  isOtpSendRefusal,
  isOwnerRole,
  sendRefusalMessage,
} from '@/shared/domain'
import { RegistrationFieldError } from '@/modules/auth/domain'
import { SUPPORT_EMAIL, isCustomerActivationLandingEnabled } from '@/shared/config'
import { reportError } from '@/shared/infra/observability'
import {
  useAuth,
  useForceTheme,
  useOtpSendCooldown,
  useSubmitGuard,
} from '@/shared/ui/hooks'
import { useToast } from '@/shared/ui/providers'
import type { ContactClaimState } from '../../register/components/use-hunter-profile-step'
import {
  EMPTY_LICENSE_ENTRY,
  type DriversLicenseEntry,
  type JoinFormValues,
  type LicenseEntry,
} from '../components/form-values'
import { computeJoinSteps, type JoinStep, type JoinStepId } from '../join-step-types'
import type { InviteRenewalState } from '../invite-renewal-state'
import {
  STEP_FIELD_PREFIXES,
  countryHasStateOptions,
  mapComplianceMissingFieldsToSteps,
  mapServerIssuesToFormErrors,
  validateFormValues,
  validateStepFields,
} from '../validate-form-values'
import { useWaiverSigner, type WaiverSignerState } from './use-waiver-signer'
import {
  clearJoinFormDraft,
  clearWaiverProgress,
  loadJoinFormDraft,
  saveJoinFormDraft,
  waiverProgressKey,
  type JoinFormDraft,
} from '../join-draft-store'

const PRO_PAYWALL_PATH_AFTER_INVITE = '/pro?source=join_crew_invite_accepted'
const FORK_ACCOUNT_NOT_FOUND_MESSAGE =
  'We couldn’t find an account for that email. You can create one instead.'
const FORK_NETWORK_MESSAGE = 'Network error. Check your connection and try again.'
const FORK_SEND_FAILED_MESSAGE = 'We couldn’t send the code. Please try again.'
const INVITE_REQUEST_FAILED_MESSAGE =
  'We couldn’t request a new invitation. Please try again.'

export type UseJoinPageProps = {
  onVerifyInvitationToken: VerifyInvitationToken
  onRequestNewInvite: RequestNewInvite
  onCompleteRegistrationFromInvitation: CompleteRegistrationFromInvitation
  onSendOtp: SendOtp
  onVerifyOtp: VerifyOtp
  onSendContactClaimOtp: SendContactClaimOtp
  onClaimContact: ClaimContact
  onSetSession: SetSession
  onLoadUserProfile: LoadUserProfile
  onRecordLegalAcceptance: RecordLegalAcceptance
  onLoadCurrentDocument: LoadCurrentDocument
  onListRequiredWaivers: ListRequiredWaivers
  onCreateSignatureRequest: CreateSignatureRequest
  onGetSignatureRequestStatus: GetSignatureRequestStatus
  onSubscribeSignatureRequestStatus: SubscribeSignatureRequestStatus
  onEmitFunnelEvent: EmitFunnelEvent
  onUploadIdentityDocument: UploadIdentityDocument
}

export type UseJoinPageResult =
  | { phase: 'verifying' }
  | { phase: 'invalid'; message: string }
  | {
      phase: 'expired'
      organizationName?: string
      renewable: boolean
      renewalState: InviteRenewalState
      renewalError: string
      cooldownSeconds: number
      onRequestNewInvite: () => void
    }
  | { phase: 'already-registered'; organizationName?: string }
  | { phase: 'already-claimed'; orgName: string; role: string; membershipModel?: MembershipModel }
  | {
      phase: 'account-fork'
      inviteEmail: string
      organizationName: string
      yesEmail: string
      isSubmitting: boolean
      cooldownSeconds: number
      onYesEmailChange: (value: string) => void
      onChooseNo: () => void
      onChooseYes: () => void
    }
  | {
      phase: 'account-link'
      inviteEmail: string
      linkEmail: string
      isSubmitting: boolean
      cooldownSeconds: number
      onLinkEmailChange: (value: string) => void
      onSendCode: () => void
      onCancel: () => void
    }
  | {
      phase: 'hijack-warning'
      existingUserEmail: string
      inviteEmail: string
      onSignOutAndContinue: () => void
      onCancel: () => void
    }
  | {
      phase: 'identity-conflict'
      channel: 'phone' | 'email'
      maskedIdentifier: string
      onSignIn: () => void
    }
  | {
      phase: 'contact-conflict'
      identifier: string
      channel: 'email' | 'phone'
      canChallenge: boolean
      supportHref: string
      isChallengeSending: boolean
      challengeCooldownSeconds: number
      onChallenge: () => void
      onEditInfo: () => void
    }
  | {
      phase: 'contact-claim'
      identifier: string
      identifierType: 'email' | 'phone'
      codeSent: boolean
      pendingInvite: boolean
      holderUnregistered: boolean
      claimCode: string
      claimError: string
      isClaiming: boolean
      isSendingClaimCode: boolean
      resendSeconds: number
      onClaimCodeChange: (value: string) => void
      onSendClaimCode: () => void
      onConfirmClaim: () => void
      onCancelClaim: () => void
    }
  | {
      phase: 'otp'
      identifier: string
      channel: 'email' | 'phone'
      onSendOtp: SendOtp
      onVerifyOtp: VerifyOtp
      onVerifySuccess: () => void
      onBack: () => void
    }
  | {
      phase: 'form'
      token: string
      tokenData: InvitationTokenData
      organization: InvitationOrganization
      preAuth?: InvitationPreAuth | null
      trustedEmail: boolean
      values: JoinFormValues
      errors: Record<string, string>
      isSubmitting: boolean
      isMinor: boolean
      age?: number
      emailLocked: boolean
      phoneRequired: boolean
      phoneLocked: boolean
      canLinkExistingAccount: boolean
      linkedEmail: string | null
      onLinkExistingAccount: () => void
      tosAccepted: boolean
      privacyAccepted: boolean
      legalAlreadyAccepted: boolean
      steps: JoinStep[]
      currentStepIndex: number
      currentStep: JoinStep
      isLastStep: boolean
      isStepSkippable: boolean
      canGoBack: boolean
      isCurrentStepComplete: boolean
      goBack: () => void
      goNext: () => void
      onSkip: () => void
      scrollContainerRef: RefObject<HTMLDivElement | null>
      licenseEntries: Record<string, LicenseEntry>
      driversLicense?: DriversLicenseEntry
      onDriversLicenseChange: (entry: DriversLicenseEntry) => void
      onFieldChange: (field: keyof JoinFormValues, value: string) => void
      onLicenseChange: (licenseType: string, field: keyof LicenseEntry, value: string | boolean) => void
      onTosChange: (value: boolean) => void
      onPrivacyChange: (value: boolean) => void
      onSubmit: () => void
      onLoadCurrentDocument: LoadCurrentDocument
      onDocumentsLoaded: (documents: LegalDocument[]) => void
      waiver: WaiverSignerState
    }
  | {
      phase: 'success'
      organizationName: string
      onSignOut: () => void
    }
  | {
      phase: 'download-app'
      organizationName: string
    }
  | {
      phase: 'returning-to-app'
      organizationName: string
    }

type VerifySuccessData = {
  tokenData: InvitationTokenData
  organization: InvitationOrganization
  preAuth?: InvitationPreAuth | null
  trustedEmail: boolean
}

type InternalPhase =
  | 'verifying'
  | 'invalid'
  | 'expired'
  | 'already-registered'
  | 'already-claimed'
  | 'account-fork'
  | 'account-link'
  | 'hijack-warning'
  | 'identity-conflict'
  | 'contact-conflict'
  | 'contact-claim'
  | 'otp'
  | 'form'
  | 'success'
  | 'download-app'
  | 'returning-to-app'

type ContactRecovery = {
  identifier: string
  channel: 'email' | 'phone'
}

function calculateAge(dateOfBirth?: string): number | undefined {
  if (!dateOfBirth) return undefined
  const dob = new Date(dateOfBirth)
  if (Number.isNaN(dob.getTime())) return undefined
  const now = new Date()
  let age = now.getFullYear() - dob.getFullYear()
  const monthDelta = now.getMonth() - dob.getMonth()
  if (monthDelta < 0 || (monthDelta === 0 && now.getDate() < dob.getDate())) {
    age -= 1
  }
  return age
}

function splitName(name?: string): { firstName: string; lastName: string } {
  if (!name) return { firstName: '', lastName: '' }
  const parts = name.trim().split(/\s+/)
  if (parts.length === 0) return { firstName: '', lastName: '' }
  if (parts.length === 1) return { firstName: parts[0], lastName: '' }
  return { firstName: parts[0], lastName: parts.slice(1).join(' ') }
}

function initialValuesFromVerify(
  tokenData: InvitationTokenData,
  preAuth: InvitationPreAuth | null | undefined,
): JoinFormValues {
  const isShared = tokenData.inviteType === 'shared'
  const profile = isShared ? undefined : preAuth?.profile
  const tokenName = isShared ? { firstName: '', lastName: '' } : splitName(tokenData.name)
  const email = isShared ? '' : (preAuth?.email ?? tokenData.email ?? '')
  return {
    firstName: profile?.firstName ?? tokenName.firstName,
    lastName: profile?.lastName ?? tokenName.lastName,
    email,
    phone: profile?.phone ?? (isShared ? '' : (tokenData.phone ?? '')),
    dateOfBirth: profile?.dateOfBirth ?? '',
    flywayRegion: profile?.flywayRegion,
    addressLine1: profile?.addressLine1,
    addressLine2: profile?.addressLine2,
    city: profile?.city,
    state: profile?.state,
    zipCode: profile?.zipCode,
    country: profile?.country,
    emergencyContact: profile?.emergencyContact,
    emergencyPhone: profile?.emergencyPhone,
    emergencyRelationship: profile?.emergencyRelationship,
    emergencyContact2: profile?.emergencyContact2,
    emergencyPhone2: profile?.emergencyPhone2,
    emergencyRelationship2: profile?.emergencyRelationship2,
    vehicleMake: profile?.vehicleMake,
    vehicleModel: profile?.vehicleModel,
    vehicleColor: profile?.vehicleColor,
    vehicleLicensePlate: profile?.vehicleLicensePlate,
    vehicleState: profile?.vehicleState,
  }
}

export function useJoinPage(props: UseJoinPageProps): UseJoinPageResult {
  const {
    onVerifyInvitationToken,
    onRequestNewInvite,
    onCompleteRegistrationFromInvitation,
    onUploadIdentityDocument,
    onSendOtp,
    onVerifyOtp,
    onSendContactClaimOtp,
    onClaimContact,
    onSetSession,
    onLoadUserProfile,
    onRecordLegalAcceptance,
    onLoadCurrentDocument,
    onListRequiredWaivers,
    onCreateSignatureRequest,
    onGetSignatureRequestStatus,
    onSubscribeSignatureRequestStatus,
    onEmitFunnelEvent,
  } = props

  const { token } = useParams<{ token: string }>()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { user, signOut } = useAuth()
  const toast = useToast()

  const restoredFormDraftRef = useRef<JoinFormDraft | null | undefined>(undefined)
  if (restoredFormDraftRef.current === undefined) {
    restoredFormDraftRef.current = token ? loadJoinFormDraft(token) : null
  }
  const restoredFormDraft = restoredFormDraftRef.current

  const appReturnRef = useRef<{ source: string | null } | undefined>(undefined)
  if (appReturnRef.current === undefined) {
    const params =
      typeof window !== 'undefined'
        ? new URLSearchParams(window.location.search)
        : new URLSearchParams()
    appReturnRef.current = { source: params.get('source') }
  }

  useForceTheme('dark')
  const { isSubmitting, runGuarded } = useSubmitGuard()
  const { isSubmitting: isChallengeSending, runGuarded: runChallengeGuarded } = useSubmitGuard()
  const { isSubmitting: isForkSending, runGuarded: runForkSendGuarded } = useSubmitGuard()
  const { isSubmitting: isSendingClaimCode, runGuarded: runClaimSendGuarded } = useSubmitGuard()
  const { runGuarded: runInviteRequestGuarded } = useSubmitGuard()

  const [isBootstrapping, setIsBootstrapping] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false
    const params = new URLSearchParams(window.location.search)
    return Boolean(params.get('access_token') && params.get('refresh_token'))
  })
  const bootstrapRef = useRef<boolean>(false)

  const [phase, setPhase] = useState<InternalPhase>('verifying')
  const [invalidMessage, setInvalidMessage] = useState<string>('This invitation link is invalid.')
  const [organizationName, setOrganizationName] = useState<string | undefined>(undefined)
  const [isRenewable, setIsRenewable] = useState<boolean>(false)
  const tokenDigest = useMemo(() => (token ? hashStorageKeyPart(token) : null), [token])
  const [renewalState, setRenewalState] = useState<InviteRenewalState>('idle')
  const [renewalError, setRenewalError] = useState<string>('')
  const {
    secondsLeft: inviteRequestSeconds,
    applyRefusal: applyInviteRequestRefusal,
    recordSend: recordInviteRequest,
  } = useOtpSendCooldown(tokenDigest ? { channel: 'invite', identifier: tokenDigest } : null)
  const [alreadyClaimedInfo, setAlreadyClaimedInfo] = useState<{
    orgName: string
    role: string
    membershipModel?: MembershipModel
  }>({
    orgName: '',
    role: '',
  })
  const [verifyData, setVerifyData] = useState<VerifySuccessData | null>(null)
  const [hijackConflict, setHijackConflict] = useState<
    | {
        existingUserEmail: string
        inviteEmail: string
        sessionTokens: { accessToken: string; refreshToken: string }
      }
    | null
  >(null)
  const [identityConflict, setIdentityConflict] = useState<InvitationIdentityConflict | null>(null)
  const [contactRecovery, setContactRecovery] = useState<ContactRecovery | null>(null)
  const [resubmitAfterRecovery, setResubmitAfterRecovery] = useState<boolean>(false)
  const [forkEmail, setForkEmail] = useState<string>('')
  const [forkYesPending, setForkYesPending] = useState<boolean>(false)
  const [otpReturnPhase, setOtpReturnPhase] = useState<'account-fork' | 'account-link'>(
    'account-fork',
  )
  const [autoAuthedFromInvite, setAutoAuthedFromInvite] = useState<boolean>(false)
  const [linkedEmail, setLinkedEmail] = useState<string | null>(null)
  const [linkedPhoneVerified, setLinkedPhoneVerified] = useState<boolean>(false)
  const [contactClaim, setContactClaim] = useState<ContactClaimState | null>(null)
  const [claimCode, setClaimCode] = useState<string>('')
  const [claimError, setClaimError] = useState<string>('')
  const [isClaiming, setIsClaiming] = useState<boolean>(false)
  const {
    secondsLeft: claimResendSeconds,
    applyRefusal: applyClaimRefusal,
    recordSend: recordClaimSend,
  } = useOtpSendCooldown(
    contactClaim
      ? { channel: contactClaim.identifierType, identifier: contactClaim.identifier }
      : null,
  )
  const {
    secondsLeft: challengeCooldownSeconds,
    applyRefusal: applyChallengeRefusal,
    recordSend: recordChallengeSend,
  } = useOtpSendCooldown(
    contactRecovery
      ? { channel: contactRecovery.channel, identifier: contactRecovery.identifier }
      : null,
  )
  const {
    secondsLeft: forkCooldownSeconds,
    applyRefusal: applyForkRefusal,
    recordSend: recordForkSend,
  } = useOtpSendCooldown({
    channel: 'email',
    identifier: forkEmail.trim(),
  })
  const [values, setValues] = useState<JoinFormValues>(
    () => restoredFormDraft?.values ?? {},
  )
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [storedStepIndex, setCurrentStepIndex] = useState<number>(
    () => restoredFormDraft?.currentStepIndex ?? 0,
  )
  const [tosAccepted, setTosAccepted] = useState<boolean>(
    () => restoredFormDraft?.tosAccepted ?? false,
  )
  const [privacyAccepted, setPrivacyAccepted] = useState<boolean>(
    () => restoredFormDraft?.privacyAccepted ?? false,
  )
  const [legalDocuments, setLegalDocuments] = useState<LegalDocument[]>([])
  const [driversLicense, setDriversLicense] = useState<DriversLicenseEntry | undefined>(
    undefined,
  )
  const [licenseEntries, setLicenseEntries] = useState<Record<string, LicenseEntry>>(
    () => restoredFormDraft?.licenseEntries ?? {},
  )
  const [skippedSteps, setSkippedSteps] = useState<JoinStepId[]>(
    () => restoredFormDraft?.skippedSteps ?? [],
  )
  const scrollContainerRef = useRef<HTMLDivElement>(null)

  const verifyCalledRef = useRef<boolean>(false)

  const emitRef = useRef<EmitFunnelEvent>(onEmitFunnelEvent)
  emitRef.current = onEmitFunnelEvent
  const registrationStartedRef = useRef<boolean>(false)
  const currentStepIdRef = useRef<JoinStepId | null>(null)
  const inFormPhaseRef = useRef<boolean>(false)

  useEffect(() => {
    if (bootstrapRef.current) return
    const accessToken = searchParams.get('access_token')
    const refreshToken = searchParams.get('refresh_token')
    if (!accessToken || !refreshToken) {
      setIsBootstrapping(false)
      return
    }
    bootstrapRef.current = true
    setIsBootstrapping(true)
    onSetSession({ accessToken, refreshToken })
      .catch((err) => {
        reportError(err, { fallbackMessage: 'Failed to start your session.' })
      })
      .finally(() => {
        if (typeof window !== 'undefined') {
          window.history.replaceState({}, '', `/join/${token ?? ''}`)
        }
        setIsBootstrapping(false)
      })
  }, [searchParams, onSetSession, token])

  useEffect(() => {
    if (isBootstrapping) return
    if (verifyCalledRef.current) return
    if (!token) {
      setPhase('invalid')
      setInvalidMessage('This invitation link is invalid.')
      return
    }
    verifyCalledRef.current = true
    setPhase('verifying')
    onVerifyInvitationToken({ token, preauth: undefined, createSession: true })
      .then((outcome) => {
        switch (outcome.kind) {
          case 'invalid':
            setInvalidMessage(outcome.message)
            setPhase('invalid')
            return
          case 'expired':
            setOrganizationName(outcome.organizationName)
            setIsRenewable(outcome.renewable)
            setPhase('expired')
            return
          case 'already-registered':
            setOrganizationName(outcome.organizationName)
            setPhase('already-registered')
            return
          case 'already-claimed':
            setAlreadyClaimedInfo({
              orgName: outcome.orgName,
              role: outcome.role,
              membershipModel: outcome.membershipModel,
            })
            setPhase('already-claimed')
            return
          case 'success': {
            const { tokenData, organization, preAuth, trustedEmail, sessionTokens } = outcome
            setVerifyData({ tokenData, organization, preAuth, trustedEmail })
            if (!restoredFormDraftRef.current) {
              setValues(initialValuesFromVerify(tokenData, preAuth))
              const requiredTypes = organization.guestRequirements?.required_license_types ?? []
              if (requiredTypes.length > 0) {
                const initialEntries: Record<string, LicenseEntry> = {}
                for (const lt of requiredTypes) {
                  initialEntries[lt] = { ...EMPTY_LICENSE_ENTRY }
                }
                setLicenseEntries(initialEntries)
              }
              const initialTos = preAuth?.legalAccepted === true
              const initialPrivacy = preAuth?.legalAccepted === true
              setTosAccepted(initialTos)
              setPrivacyAccepted(initialPrivacy)
            }

            const currentEmail = user?.email ?? null
            const inviteEmail = tokenData.email ?? preAuth?.email ?? ''

            if (sessionTokens) {
              if (
                currentEmail &&
                inviteEmail &&
                currentEmail.trim().toLowerCase() !==
                  inviteEmail.trim().toLowerCase()
              ) {
                setHijackConflict({
                  existingUserEmail: currentEmail,
                  inviteEmail,
                  sessionTokens,
                })
                setPhase('hijack-warning')
                return
              }
              onSetSession({
                accessToken: sessionTokens.accessToken,
                refreshToken: sessionTokens.refreshToken,
              })
                .then(() => {
                  setAutoAuthedFromInvite(true)
                  setPhase('form')
                })
                .catch((err) => {
                  reportError(err, { fallbackMessage: 'Failed to start your session.' })
                  setInvalidMessage('Unable to start your session. Please try the link again.')
                  setPhase('invalid')
                })
              return
            }

            if (currentEmail) {
              setPhase('form')
              return
            }

            if (outcome.identityConflict) {
              setIdentityConflict(outcome.identityConflict)
              setPhase('identity-conflict')
              return
            }

            const isCustomerActivation = tokenData.activationExperience === 'customer'

            if (isCustomerActivation) {
              if (!outcome.noAccount) {
                setPhase('otp')
                return
              }
              onVerifyInvitationToken({
                token,
                preauth: undefined,
                createSession: true,
                createForNewAccount: true,
              })
                .then((activated) => {
                  if (activated.kind !== 'success') {
                    setInvalidMessage('Unable to verify invitation. Please try again later.')
                    setPhase('invalid')
                    return
                  }
                  if (activated.identityConflict) {
                    setIdentityConflict(activated.identityConflict)
                    setPhase('identity-conflict')
                    return
                  }
                  if (!activated.sessionTokens) {
                    setPhase('otp')
                    return
                  }
                  onSetSession({
                    accessToken: activated.sessionTokens.accessToken,
                    refreshToken: activated.sessionTokens.refreshToken,
                  })
                    .then(() => {
                      setPhase('form')
                    })
                    .catch((err) => {
                      reportError(err, { fallbackMessage: 'Failed to start your session.' })
                      setInvalidMessage('Unable to start your session. Please try the link again.')
                      setPhase('invalid')
                    })
                })
                .catch((err) => {
                  reportError(err, { fallbackMessage: 'Failed to load invitation.' })
                  setInvalidMessage('Unable to verify invitation. Please try again later.')
                  setPhase('invalid')
                })
              return
            }

            if (outcome.noAccount) {
              setPhase('account-fork')
              return
            }

            setPhase('otp')
            return
          }
        }
      })
      .catch((err) => {
        reportError(err, { fallbackMessage: 'Failed to load invitation.' })
        setInvalidMessage('Unable to verify invitation. Please try again later.')
        setPhase('invalid')
      })
  }, [token, onVerifyInvitationToken, onSetSession, user, isBootstrapping])

  const handleFieldChange = useCallback(
    (field: keyof JoinFormValues, value: string) => {
      const clearedErrorKeys = field === 'country' ? ['country', 'state'] : [field as string]
      setValues((prev) =>
        field === 'country'
          ? { ...prev, country: value, state: '' }
          : { ...prev, [field]: value },
      )
      setErrors((prev) => {
        if (!clearedErrorKeys.some((key) => prev[key])) return prev
        const next = { ...prev }
        for (const key of clearedErrorKeys) delete next[key]
        return next
      })
    },
    [],
  )

  const handleTosChange = useCallback((value: boolean) => {
    setTosAccepted(value)
    setErrors((prev) => {
      if (!prev.legalDocuments) return prev
      const next = { ...prev }
      delete next.legalDocuments
      return next
    })
  }, [])

  const handlePrivacyChange = useCallback((value: boolean) => {
    setPrivacyAccepted(value)
    setErrors((prev) => {
      if (!prev.legalDocuments) return prev
      const next = { ...prev }
      delete next.legalDocuments
      return next
    })
  }, [])

  const handleDocumentsLoaded = useCallback((documents: LegalDocument[]) => {
    setLegalDocuments(documents)
  }, [])

  const handleLicenseChange = useCallback(
    (licenseType: string, field: keyof LicenseEntry, value: string | boolean) => {
      setLicenseEntries((prev) => ({
        ...prev,
        [licenseType]: {
          ...(prev[licenseType] ?? EMPTY_LICENSE_ENTRY),
          [field]: value,
        },
      }))
      setErrors((prev) => {
        const errorKey = `licenseEntries.${licenseType}.${field}`
        if (!prev[errorKey]) return prev
        const next = { ...prev }
        delete next[errorKey]
        return next
      })
    },
    [],
  )

  const inviteToExistingAccount = verifyData?.preAuth?.registered === true

  const handleOtpVerifySuccess = useCallback(() => {
    if (contactRecovery) {
      setContactRecovery(null)
      setResubmitAfterRecovery(true)
    }
    setPhase('form')
  }, [contactRecovery])

  const handleOtpBack = useCallback(() => {
    if (contactRecovery) {
      setPhase('contact-conflict')
      return
    }
    navigate('/sign-in', { replace: true })
  }, [navigate, contactRecovery])

  const handleConflictSignIn = useCallback(() => {
    navigate(`/sign-in?redirectTo=${encodeURIComponent(`/join/${token ?? ''}`)}`, {
      replace: true,
    })
  }, [navigate, token])

  const handleChallengeContact = useCallback(async () => {
    if (!contactRecovery || inviteToExistingAccount || challengeCooldownSeconds > 0) return
    await runChallengeGuarded(async () => {
      try {
        await onSendOtp({
          identifier: contactRecovery.identifier,
          channel: contactRecovery.channel,
          allowCreate: false,
        })
        recordChallengeSend()
        setPhase('otp')
      } catch (err) {
        applyChallengeRefusal(err)
        const message = isOtpSendRefusal(err)
          ? sendRefusalMessage(err, false)
          : reportError(err, {
              fallbackMessage: 'Failed to send the code. Please try again.',
            })
        toast.error('Could not send code', message)
      }
    })
  }, [
    applyChallengeRefusal,
    challengeCooldownSeconds,
    contactRecovery,
    inviteToExistingAccount,
    onSendOtp,
    recordChallengeSend,
    runChallengeGuarded,
    toast,
  ])

  const handleEditContactInfo = useCallback(() => {
    setContactRecovery(null)
    setPhase('form')
  }, [])

  const handleSignOutAndContinue = useCallback(async () => {
    if (!hijackConflict) return
    try {
      await signOut()
      await onSetSession({
        accessToken: hijackConflict.sessionTokens.accessToken,
        refreshToken: hijackConflict.sessionTokens.refreshToken,
      })
      setHijackConflict(null)
      setAutoAuthedFromInvite(true)
      setPhase('form')
    } catch (err) {
      reportError(err, { fallbackMessage: 'Failed to switch sessions.' })
      toast.error('Sign out failed', 'Please try again.')
    }
  }, [hijackConflict, signOut, onSetSession, toast])

  const handleHijackCancel = useCallback(() => {
    navigate('/dashboard', { replace: true })
  }, [navigate])

  const handleForkChooseNo = useCallback(() => {
    if (!token) return
    void runForkSendGuarded(() =>
      onVerifyInvitationToken({
        token,
        preauth: undefined,
        createSession: true,
        createForNewAccount: true,
      })
        .then((outcome) => {
          if (outcome.kind !== 'success') {
            setInvalidMessage('Unable to verify invitation. Please try again later.')
            setPhase('invalid')
            return
          }
          if (outcome.identityConflict) {
            setIdentityConflict(outcome.identityConflict)
            setPhase('identity-conflict')
            return
          }
          const { sessionTokens } = outcome
          if (!sessionTokens) {
            setPhase('otp')
            return
          }
          onSetSession({
            accessToken: sessionTokens.accessToken,
            refreshToken: sessionTokens.refreshToken,
          })
            .then(() => {
              setPhase('form')
            })
            .catch((err) => {
              reportError(err, { fallbackMessage: 'Failed to start your session.' })
              setInvalidMessage('Unable to start your session. Please try the link again.')
              setPhase('invalid')
            })
        })
        .catch((err) => {
          reportError(err, { fallbackMessage: 'Failed to load invitation.' })
          setInvalidMessage('Unable to verify invitation. Please try again later.')
          setPhase('invalid')
        }),
    )
  }, [token, onVerifyInvitationToken, onSetSession, runForkSendGuarded])

  const handleOpenAccountLink = useCallback(() => {
    setForkEmail('')
    setOtpReturnPhase('account-link')
    setPhase('account-link')
  }, [])

  const handleAccountLinkCancel = useCallback(() => {
    setOtpReturnPhase('account-fork')
    setPhase('form')
  }, [])

  const handleForkChooseYes = useCallback(async () => {
    const email = forkEmail.trim()
    if (!email || forkCooldownSeconds > 0) return
    await runForkSendGuarded(async () => {
      try {
        await onSendOtp({ identifier: email, channel: 'email', allowCreate: false })
        recordForkSend()
        setForkYesPending(true)
        setPhase('otp')
      } catch (err) {
        applyForkRefusal(err)
        if (err instanceof UserNotFoundError) {
          toast.error('No account found', FORK_ACCOUNT_NOT_FOUND_MESSAGE)
          return
        }
        if (isOtpSendRefusal(err) || err instanceof EmailLinkedToDifferentAccountError) {
          toast.error('Could not send code', err.message)
          return
        }
        const message =
          err instanceof NetworkError
            ? FORK_NETWORK_MESSAGE
            : reportError(err, { fallbackMessage: FORK_SEND_FAILED_MESSAGE })
        toast.error('Could not send code', message)
      }
    })
  }, [
    applyForkRefusal,
    forkCooldownSeconds,
    forkEmail,
    onSendOtp,
    recordForkSend,
    runForkSendGuarded,
    toast,
  ])

  const handleForkOtpVerifySuccess = useCallback(() => {
    const provenEmail = forkEmail.trim()
    setForkYesPending(false)
    setLinkedEmail(provenEmail)
    setLinkedPhoneVerified(false)
    setValues((prev) => ({ ...prev, email: provenEmail }))
    setErrors({})
    setPhase('form')

    onLoadUserProfile()
      .then((result) => {
        if (!result.exists) return
        const { user } = result
        setLinkedPhoneVerified(Boolean(user.phone && user.phoneVerifiedAt))
        setValues((prev) => ({
          ...prev,
          firstName: user.firstName || prev.firstName,
          lastName: user.lastName || prev.lastName,
          email: provenEmail,
          phone: user.phone || prev.phone,
          dateOfBirth: user.dateOfBirth ?? prev.dateOfBirth,
          flywayRegion: user.flywayRegion ?? prev.flywayRegion,
          addressLine1: user.addressLine1 ?? prev.addressLine1,
          addressLine2: user.addressLine2 ?? prev.addressLine2,
          city: user.city ?? prev.city,
          state: user.state ?? prev.state,
          zipCode: user.zipCode ?? prev.zipCode,
          country: user.country ?? prev.country,
          emergencyContact: user.emergencyContact ?? prev.emergencyContact,
          emergencyPhone: user.emergencyPhone ?? prev.emergencyPhone,
          emergencyRelationship: user.emergencyRelationship ?? prev.emergencyRelationship,
          emergencyContact2: user.emergencyContact2 ?? prev.emergencyContact2,
          emergencyPhone2: user.emergencyPhone2 ?? prev.emergencyPhone2,
          emergencyRelationship2: user.emergencyRelationship2 ?? prev.emergencyRelationship2,
          vehicleMake: user.vehicleMake ?? prev.vehicleMake,
          vehicleModel: user.vehicleModel ?? prev.vehicleModel,
          vehicleColor: user.vehicleColor ?? prev.vehicleColor,
          vehicleLicensePlate: user.vehicleLicensePlate ?? prev.vehicleLicensePlate,
          vehicleState: user.vehicleState ?? prev.vehicleState,
        }))
      })
      .catch((err) => {
        reportError(err, {
          fallbackMessage: 'Signed in, but we could not load your saved details.',
        })
      })
  }, [forkEmail, onLoadUserProfile])

  const handleForkOtpBack = useCallback(() => {
    setForkYesPending(false)
    setPhase(otpReturnPhase)
  }, [otpReturnPhase])

  const tokenDataMemo = verifyData?.tokenData
  const organizationMemo = verifyData?.organization

  const age = calculateAge(values.dateOfBirth)
  const isMinor = typeof age === 'number' && age < 18

  const waiverRequired = organizationMemo?.guestRequirements?.liability_waiver === true

  const waiver = useWaiverSigner({
    enabled: phase === 'form' && waiverRequired,
    orgId: tokenDataMemo?.orgId ?? '',
    orgType: tokenDataMemo?.orgType === 'outfitter' ? 'outfitter' : 'club',
    token: token ?? '',
    guestData: {
      firstName: values.firstName ?? '',
      lastName: values.lastName ?? '',
      email: values.email ?? '',
      dateOfBirth: values.dateOfBirth || undefined,
    },
    isMinor,
    age,
    onListRequiredWaivers,
    onCreateSignatureRequest,
    onGetSignatureRequestStatus,
    onSubscribeSignatureRequestStatus,
  })

  const waiverComplete = !waiverRequired || waiver.allSigned

  useEffect(() => {
    if (phase !== 'form') return
    if (!token) return
    saveJoinFormDraft(token, {
      values,
      currentStepIndex: storedStepIndex,
      tosAccepted,
      privacyAccepted,
      licenseEntries,
      skippedSteps,
    })
  }, [phase, token, values, storedStepIndex, tosAccepted, privacyAccepted, licenseEntries, skippedSteps])

  useEffect(() => {
    if (phase !== 'form') return
    if (typeof window !== 'undefined' && typeof window.scrollTo === 'function') {
      window.scrollTo({ top: 0 })
    }
    const container = scrollContainerRef.current
    if (container && typeof container.scrollTo === 'function') {
      container.scrollTo({ top: 0 })
    }
  }, [phase, storedStepIndex])

  const isRegisteredReclaim = verifyData?.preAuth?.registered === true || linkedEmail !== null

  const emailLocked = useMemo(() => {
    if (!verifyData) return false
    if (isRegisteredReclaim) return true
    const { tokenData, trustedEmail } = verifyData
    if (tokenData.inviteType === 'shared') return false
    return trustedEmail && Boolean(tokenData.email)
  }, [verifyData, isRegisteredReclaim])

  const accountPhoneVerified =
    linkedEmail !== null ? linkedPhoneVerified : verifyData?.preAuth?.phoneVerified === true
  const phoneLocked = isRegisteredReclaim && accountPhoneVerified

  const steps = useMemo<JoinStep[]>(() => {
    if (!organizationMemo?.guestRequirements) {
      return [{ id: 'personal_info', label: 'Personal Info', mode: 'required' }]
    }
    return computeJoinSteps(organizationMemo.guestRequirements)
  }, [organizationMemo])

  const currentStepIndex = Math.min(storedStepIndex, steps.length - 1)
  const currentStep = steps[currentStepIndex]
  const isLastStep = currentStepIndex === steps.length - 1
  const canGoBack = currentStepIndex > 0
  const isStepSkippable = currentStep?.mode === 'optional'

  const inFormPhase = phase === 'form'
  inFormPhaseRef.current = inFormPhase
  const currentStepId = currentStep?.id ?? null
  currentStepIdRef.current = currentStepId

  useEffect(() => {
    if (phase !== 'form') return
    if (!currentStepId) return
    if (!registrationStartedRef.current) {
      registrationStartedRef.current = true
      emitRef.current({ eventType: 'registration_started' })
    }
    emitRef.current({ eventType: 'registration_step_viewed', step: currentStepId })
  }, [phase, currentStepId])

  useEffect(() => {
    if (typeof window === 'undefined') return
    const handleBeforeUnload = () => {
      if (!inFormPhaseRef.current) return
      if (!currentStepIdRef.current) return
      emitRef.current({ eventType: 'registration_abandoned', step: currentStepIdRef.current })
    }
    window.addEventListener('beforeunload', handleBeforeUnload)
    return () => window.removeEventListener('beforeunload', handleBeforeUnload)
  }, [])

  const isCurrentStepComplete = useMemo(() => {
    if (!organizationMemo?.guestRequirements || !currentStep) return false
    const stepErrors = validateStepFields({
      values: { ...values, licenseEntries, driversLicense },
      requirements: organizationMemo.guestRequirements,
      tosAccepted,
      privacyAccepted,
      legalAlreadyAccepted: verifyData?.preAuth?.legalAccepted === true,
      phoneLocked,
      waiverComplete,
      stepId: currentStep.id,
    })
    return Object.keys(stepErrors).length === 0
  }, [organizationMemo, currentStep, values, licenseEntries, driversLicense, tosAccepted, privacyAccepted, verifyData, phoneLocked, waiverComplete])

  const handleGoBack = useCallback(() => {
    setErrors({})
    setCurrentStepIndex(Math.max(0, currentStepIndex - 1))
  }, [currentStepIndex])

  const handleGoNext = useCallback(() => {
    if (!organizationMemo || !currentStep) return
    const stepErrors = validateStepFields({
      values: { ...values, licenseEntries, driversLicense },
      requirements: organizationMemo.guestRequirements,
      tosAccepted,
      privacyAccepted,
      legalAlreadyAccepted: verifyData?.preAuth?.legalAccepted === true,
      phoneLocked,
      waiverComplete,
      stepId: currentStep.id,
    })
    if (Object.keys(stepErrors).length > 0) {
      setErrors(stepErrors)
      toast.error('Please fix the highlighted fields')
      return
    }
    setErrors({})
    emitRef.current({ eventType: 'registration_step_completed', step: currentStep.id })
    setCurrentStepIndex((prev) => prev + 1)
  }, [organizationMemo, currentStep, values, licenseEntries, driversLicense, tosAccepted, privacyAccepted, verifyData, phoneLocked, waiverComplete, toast])

  const handleSubmit = useCallback(async (skippedOverride?: JoinStepId[]) => {
    if (!verifyData || !tokenDataMemo || !organizationMemo || !token) return

    const effectiveSkipped = skippedOverride ?? skippedSteps
    const validationErrors = validateFormValues({
      values: { ...values, licenseEntries, driversLicense },
      requirements: organizationMemo.guestRequirements,
      tosAccepted,
      privacyAccepted,
      legalAlreadyAccepted: verifyData.preAuth?.legalAccepted === true,
      phoneLocked,
      waiverComplete,
      skippedSteps: effectiveSkipped,
    })

    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors)
      const errorKeys = Object.keys(validationErrors)
      const firstStepWithError = steps.findIndex((step) => {
        return errorKeys.some((key) => STEP_FIELD_PREFIXES[step.id].some((p) => key.startsWith(p)))
      })
      if (firstStepWithError >= 0) {
        setCurrentStepIndex(firstStepWithError)
      }
      toast.error('Please fix the highlighted fields')
      return
    }

    await runGuarded(async () => {
      try {
        const userData: CompleteRegistrationUserData = {
          firstName: values.firstName ?? '',
          lastName: values.lastName ?? '',
          email: values.email ?? '',
          phone: phoneLocked ? undefined : values.phone || undefined,
          dateOfBirth: values.dateOfBirth || undefined,
          addressLine1: values.addressLine1 || undefined,
          addressLine2: values.addressLine2 || undefined,
          city: values.city || undefined,
          ...(countryHasStateOptions(values.country)
            ? { state: values.state || undefined }
            : {}),
          zipCode: values.zipCode || undefined,
          country: values.country || undefined,
          emergencyContact: values.emergencyContact || undefined,
          emergencyPhone: values.emergencyPhone || undefined,
          emergencyRelationship: values.emergencyRelationship || undefined,
          emergencyContact2: values.emergencyContact2 || undefined,
          emergencyPhone2: values.emergencyPhone2 || undefined,
          emergencyRelationship2: values.emergencyRelationship2 || undefined,
          vehicleMake: values.vehicleMake || undefined,
          vehicleModel: values.vehicleModel || undefined,
          vehicleColor: values.vehicleColor || undefined,
          vehicleLicensePlate: values.vehicleLicensePlate || undefined,
          vehicleState: values.vehicleState || undefined,
          flywayRegion: values.flywayRegion || undefined,
          signatureRequestIds: waiver.signatureRequestIds,
        }

        const orgState = organizationMemo.guestRequirements?.org_state ?? null
        const licenses: Array<{
          licenseType: string
          licenseNumber: string
          expiry?: string
          state: string
          documentUrl?: string
        }> = []
        const exemptions: Array<{ licenseType: string }> = []

        for (const [licenseType, entry] of Object.entries(licenseEntries)) {
          if (entry.exempt) {
            exemptions.push({ licenseType })
          } else if (entry.licenseNumber.trim()) {
            licenses.push({
              licenseType,
              licenseNumber: entry.licenseNumber.trim(),
              expiry: entry.expiry || undefined,
              state: orgState ?? values.state ?? '',
              documentUrl: entry.documentUrl,
            })
          }
        }

        if (
          organizationMemo.guestRequirements?.driversLicense === true &&
          driversLicense?.fileBase64
        ) {
          await onUploadIdentityDocument({
            documentType: 'drivers_license',
            issuingState: driversLicense.issuingState.toUpperCase(),
            documentNumber: driversLicense.documentNumber.trim(),
            expiry: driversLicense.expiry || undefined,
            file: driversLicense.fileBase64,
            contentType: driversLicense.contentType,
          })
        }

        const registration = await onCompleteRegistrationFromInvitation({
          token,
          organizationId: tokenDataMemo.orgId,
          organizationType: tokenDataMemo.orgType ?? 'club',
          acceptTerms: true,
          role: tokenDataMemo.role,
          userData,
          licenses: licenses.length > 0 ? licenses : undefined,
          exemptions: exemptions.length > 0 ? exemptions : undefined,
        })

        clearJoinFormDraft(token)
        clearWaiverProgress(waiverProgressKey(token))

        const finalStep = steps[steps.length - 1]
        if (finalStep) {
          emitRef.current({ eventType: 'registration_step_completed', step: finalStep.id })
        }

        if (verifyData.preAuth?.legalAccepted !== true) {
          try {
            await Promise.all(
              legalDocuments.map((document) =>
                onRecordLegalAcceptance({
                  documentId: document.documentId,
                  acceptanceMethod: 'registration',
                }),
              ),
            )
          } catch (err) {
            reportError(err, { fallbackMessage: 'Failed to record legal acceptances.' })
          }
        }

        if (registration?.requiresPro === true) {
          navigate(
            appReturnRef.current?.source === 'mobile'
              ? `${PRO_PAYWALL_PATH_AFTER_INVITE}&app=mobile`
              : PRO_PAYWALL_PATH_AFTER_INVITE,
            { replace: true },
          )
          return
        }

        if (appReturnRef.current?.source === 'mobile') {
          setPhase('returning-to-app')
          return
        }

        if (
          tokenDataMemo.activationExperience === 'customer' &&
          isCustomerActivationLandingEnabled()
        ) {
          setPhase('download-app')
          return
        }

        if (isOwnerRole(tokenDataMemo.role, tokenDataMemo.orgType)) {
          window.location.href = '/dashboard'
        } else {
          setPhase('success')
        }
      } catch (err) {
        if (err instanceof ContactClaimRequiredError) {
          setContactClaim({
            identifier: err.identifier,
            identifierType: err.identifierType,
            codeSent: false,
            pendingInvite: err.pendingInvite,
            holderUnregistered: err.holderUnregistered,
          })
          setClaimCode('')
          setClaimError('')
          setPhase('contact-claim')
          return
        }
        if (err instanceof AccountExistsError) {
          setContactRecovery({ identifier: err.identifier, channel: err.identifierType })
          setPhase('contact-conflict')
          return
        }
        if (err instanceof RegistrationFieldError) {
          const fieldErrors = mapServerIssuesToFormErrors(err.issues)
          const errorKeys = Object.keys(fieldErrors)
          if (errorKeys.length > 0) {
            setErrors(fieldErrors)
            const owningStep = steps.findIndex((step) =>
              errorKeys.some((key) =>
                STEP_FIELD_PREFIXES[step.id].some((prefix) => key.startsWith(prefix)),
              ),
            )
            if (owningStep >= 0) setCurrentStepIndex(owningStep)
            toast.error('Please fix the highlighted fields', err.message)
            return
          }
        }
        if (err instanceof ComplianceIncompleteError) {
          const owningStepIds = mapComplianceMissingFieldsToSteps(err.missingFields)
          const owningStep = steps.findIndex((step) => owningStepIds.includes(step.id))
          if (owningStep >= 0) setCurrentStepIndex(owningStep)
          const missingLabels = steps
            .filter((step) => owningStepIds.includes(step.id))
            .map((step) => step.label)
          const message =
            missingLabels.length > 0
              ? `Please complete: ${missingLabels.join(', ')}`
              : err.message
          toast.error('Please complete your profile', message)
          return
        }
        if (
          err instanceof IdentityMergeConflictError ||
          err instanceof PlaceholderMergeLedgerConflictError
        ) {
          toast.error('Could not accept the invitation', err.message)
          return
        }
        if (err instanceof DuplicateContactError) {
          const isPhoneConflict = err.message.toLowerCase().includes('phone')
          const identifier = isPhoneConflict ? values.phone : values.email
          if (identifier) {
            setContactRecovery({
              identifier,
              channel: isPhoneConflict ? 'phone' : 'email',
            })
            setPhase('contact-conflict')
            return
          }
        }
        const message = reportError(err, {
          fallbackMessage: 'Registration failed. Please try again.',
        })
        toast.error('Registration failed', message)
      }
    })
  }, [
    verifyData,
    phoneLocked,
    tokenDataMemo,
    organizationMemo,
    token,
    values,
    licenseEntries,
    driversLicense,
    tosAccepted,
    privacyAccepted,
    waiverComplete,
    waiver.signatureRequestIds,
    onCompleteRegistrationFromInvitation,
    onUploadIdentityDocument,
    onRecordLegalAcceptance,
    legalDocuments,
    steps,
    skippedSteps,
    toast,
    runGuarded,
    navigate,
  ])

  const handleSubmitRef = useRef(handleSubmit)
  handleSubmitRef.current = handleSubmit

  useEffect(() => {
    if (!resubmitAfterRecovery) return
    if (phase !== 'form') return
    setResubmitAfterRecovery(false)
    void handleSubmitRef.current()
  }, [resubmitAfterRecovery, phase])

  const handleSendClaimCode = useCallback(async () => {
    if (!contactClaim || claimResendSeconds > 0) return
    setClaimError('')
    await runClaimSendGuarded(async () => {
      try {
        await onSendContactClaimOtp({
          identifier: contactClaim.identifier,
          identifierType: contactClaim.identifierType,
        })
        setContactClaim((prev) => (prev ? { ...prev, codeSent: true } : prev))
        recordClaimSend()
      } catch (err) {
        applyClaimRefusal(err)
        if (isOtpSendRefusal(err)) {
          setContactClaim((prev) => (prev ? { ...prev, codeSent: true } : prev))
        }
        setClaimError(
          isOtpSendRefusal(err)
            ? sendRefusalMessage(err, false)
            : reportError(err, { fallbackMessage: 'Failed to send the code. Please try again.' }),
        )
      }
    })
  }, [
    applyClaimRefusal,
    claimResendSeconds,
    contactClaim,
    onSendContactClaimOtp,
    recordClaimSend,
    runClaimSendGuarded,
  ])

  const handleConfirmClaim = useCallback(async () => {
    if (!contactClaim || claimCode.trim() === '') return
    setIsClaiming(true)
    setClaimError('')
    try {
      await onClaimContact({
        identifier: contactClaim.identifier,
        identifierType: contactClaim.identifierType,
        code: claimCode.trim(),
      })
      setContactClaim(null)
      setClaimCode('')
      setPhase('form')
      setResubmitAfterRecovery(true)
    } catch (err) {
      setClaimError(
        reportError(err, { fallbackMessage: 'Verification failed. Please try again.' }),
      )
    } finally {
      setIsClaiming(false)
    }
  }, [contactClaim, claimCode, onClaimContact])

  const handleCancelClaim = useCallback(() => {
    if (!contactClaim) return
    const field = contactClaim.identifierType === 'email' ? 'email' : 'phone'
    const message =
      contactClaim.identifierType === 'email'
        ? 'This email is on another account. Verify it to claim it, or use a different email.'
        : 'This phone number is on another account. Verify it to claim it, or use a different phone number.'
    setContactClaim(null)
    setClaimCode('')
    setClaimError('')
    setErrors({ [field]: message })
    const stepWithField = steps.findIndex((step) =>
      STEP_FIELD_PREFIXES[step.id].includes(field),
    )
    if (stepWithField >= 0) {
      setCurrentStepIndex(stepWithField)
    }
    setPhase('form')
  }, [contactClaim, steps])

  const handleRequestNewInvite = useCallback(async () => {
    if (!token || !tokenDigest || inviteRequestSeconds > 0) return
    await runInviteRequestGuarded(async () => {
      setRenewalState('sending')
      setRenewalError('')
      try {
        await onRequestNewInvite({ token })
        recordInviteRequest()
        setRenewalState('sent')
      } catch (err) {
        applyInviteRequestRefusal(err)
        setRenewalState('idle')
        setRenewalError(
          isOtpSendRefusal(err)
            ? err.message
            : reportError(err, { fallbackMessage: INVITE_REQUEST_FAILED_MESSAGE }),
        )
      }
    })
  }, [
    applyInviteRequestRefusal,
    inviteRequestSeconds,
    onRequestNewInvite,
    recordInviteRequest,
    runInviteRequestGuarded,
    token,
    tokenDigest,
  ])

  const handleSkip = useCallback(() => {
    if (!currentStep) return
    const stepId = currentStep.id
    const nextSkipped = skippedSteps.includes(stepId)
      ? skippedSteps
      : [...skippedSteps, stepId]
    setSkippedSteps(nextSkipped)
    setErrors({})
    emitRef.current({ eventType: 'registration_step_completed', step: stepId })
    if (isLastStep) {
      void handleSubmit(nextSkipped)
    } else {
      setCurrentStepIndex((prev) => prev + 1)
    }
  }, [currentStep, isLastStep, skippedSteps, handleSubmit])

  if (phase === 'verifying') {
    return { phase: 'verifying' }
  }

  if (phase === 'invalid') {
    return { phase: 'invalid', message: invalidMessage }
  }

  if (phase === 'expired') {
    return {
      phase: 'expired',
      organizationName,
      renewable: isRenewable,
      renewalState,
      renewalError,
      cooldownSeconds: inviteRequestSeconds,
      onRequestNewInvite: () => {
        void handleRequestNewInvite()
      },
    }
  }

  if (phase === 'already-registered') {
    return { phase: 'already-registered', organizationName }
  }

  if (phase === 'already-claimed') {
    return {
      phase: 'already-claimed',
      orgName: alreadyClaimedInfo.orgName,
      role: alreadyClaimedInfo.role,
      membershipModel: alreadyClaimedInfo.membershipModel,
    }
  }

  if (phase === 'account-fork' && verifyData) {
    return {
      phase: 'account-fork',
      inviteEmail: verifyData.tokenData.email ?? '',
      organizationName: verifyData.organization.name,
      yesEmail: forkEmail,
      isSubmitting: isForkSending,
      cooldownSeconds: forkCooldownSeconds,
      onYesEmailChange: setForkEmail,
      onChooseNo: handleForkChooseNo,
      onChooseYes: handleForkChooseYes,
    }
  }

  if (phase === 'hijack-warning' && hijackConflict) {
    return {
      phase: 'hijack-warning',
      existingUserEmail: hijackConflict.existingUserEmail,
      inviteEmail: hijackConflict.inviteEmail,
      onSignOutAndContinue: () => {
        void handleSignOutAndContinue()
      },
      onCancel: handleHijackCancel,
    }
  }

  if (phase === 'identity-conflict' && identityConflict) {
    return {
      phase: 'identity-conflict',
      channel: identityConflict.channel,
      maskedIdentifier: identityConflict.maskedIdentifier,
      onSignIn: handleConflictSignIn,
    }
  }

  if (phase === 'contact-conflict' && contactRecovery) {
    const contactLabel = contactRecovery.channel === 'phone' ? 'phone number' : 'email'
    const supportBody = [
      `I'm accepting an invitation to ${verifyData?.tokenData.orgName ?? 'an organization'} as ${values.email ?? ''}.`,
      `The ${contactLabel} ${contactRecovery.identifier} is tied to another BlindBook account. Please help me merge the accounts.`,
    ].join('\n\n')
    const supportHref = `mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent('Merge my BlindBook accounts')}&body=${encodeURIComponent(supportBody)}`
    return {
      phase: 'contact-conflict',
      identifier: contactRecovery.identifier,
      channel: contactRecovery.channel,
      canChallenge: !inviteToExistingAccount,
      supportHref,
      isChallengeSending,
      challengeCooldownSeconds,
      onChallenge: handleChallengeContact,
      onEditInfo: handleEditContactInfo,
    }
  }

  if (phase === 'contact-claim' && contactClaim) {
    return {
      phase: 'contact-claim',
      identifier: contactClaim.identifier,
      identifierType: contactClaim.identifierType,
      codeSent: contactClaim.codeSent || claimResendSeconds > 0,
      pendingInvite: contactClaim.pendingInvite,
      holderUnregistered: contactClaim.holderUnregistered,
      claimCode,
      claimError,
      isClaiming,
      isSendingClaimCode,
      resendSeconds: claimResendSeconds,
      onClaimCodeChange: setClaimCode,
      onSendClaimCode: handleSendClaimCode,
      onConfirmClaim: () => {
        void handleConfirmClaim()
      },
      onCancelClaim: handleCancelClaim,
    }
  }

  if (phase === 'account-link' && verifyData) {
    return {
      phase: 'account-link',
      inviteEmail: verifyData.tokenData.email ?? '',
      linkEmail: forkEmail,
      isSubmitting: isForkSending,
      cooldownSeconds: forkCooldownSeconds,
      onLinkEmailChange: setForkEmail,
      onSendCode: handleForkChooseYes,
      onCancel: handleAccountLinkCancel,
    }
  }

  if (phase === 'otp' && forkYesPending) {
    return {
      phase: 'otp',
      identifier: forkEmail.trim(),
      channel: 'email',
      onSendOtp,
      onVerifyOtp,
      onVerifySuccess: handleForkOtpVerifySuccess,
      onBack: handleForkOtpBack,
    }
  }

  if (phase === 'otp' && contactRecovery) {
    return {
      phase: 'otp',
      identifier: contactRecovery.identifier,
      channel: contactRecovery.channel,
      onSendOtp,
      onVerifyOtp,
      onVerifySuccess: handleOtpVerifySuccess,
      onBack: handleOtpBack,
    }
  }

  if (phase === 'otp' && verifyData) {
    const identifier = verifyData.tokenData.email ?? verifyData.tokenData.phone ?? ''
    const channel: 'email' | 'phone' = verifyData.tokenData.email ? 'email' : 'phone'
    return {
      phase: 'otp',
      identifier,
      channel,
      onSendOtp,
      onVerifyOtp,
      onVerifySuccess: handleOtpVerifySuccess,
      onBack: handleOtpBack,
    }
  }

  if (phase === 'form' && verifyData && tokenDataMemo && organizationMemo) {
    return {
      phase: 'form',
      token: token ?? '',
      tokenData: tokenDataMemo,
      organization: organizationMemo,
      preAuth: verifyData.preAuth,
      trustedEmail: verifyData.trustedEmail,
      values,
      errors,
      isSubmitting,
      isMinor,
      age,
      emailLocked,
      phoneRequired: !phoneLocked,
      phoneLocked,
      canLinkExistingAccount:
        autoAuthedFromInvite && linkedEmail === null && !inviteToExistingAccount,
      linkedEmail,
      onLinkExistingAccount: handleOpenAccountLink,
      tosAccepted,
      privacyAccepted,
      legalAlreadyAccepted: verifyData.preAuth?.legalAccepted === true,
      steps,
      currentStepIndex,
      currentStep,
      isLastStep,
      isStepSkippable,
      canGoBack,
      isCurrentStepComplete,
      goBack: handleGoBack,
      goNext: handleGoNext,
      onSkip: handleSkip,
      scrollContainerRef,
      licenseEntries,
      driversLicense,
      onDriversLicenseChange: setDriversLicense,
      onFieldChange: handleFieldChange,
      onLicenseChange: handleLicenseChange,
      onTosChange: handleTosChange,
      onPrivacyChange: handlePrivacyChange,
      onSubmit: () => {
        void handleSubmit()
      },
      onLoadCurrentDocument,
      onDocumentsLoaded: handleDocumentsLoaded,
      waiver,
    }
  }

  if (phase === 'success' && organizationMemo) {
    return {
      phase: 'success',
      organizationName: organizationMemo.name,
      onSignOut: () => {
        void signOut()
      },
    }
  }

  if (phase === 'download-app' && organizationMemo) {
    return {
      phase: 'download-app',
      organizationName: organizationMemo.name,
    }
  }

  if (phase === 'returning-to-app' && organizationMemo) {
    return {
      phase: 'returning-to-app',
      organizationName: organizationMemo.name,
    }
  }

  return { phase: 'verifying' }
}
